/*
 * Copyright (c) 2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Company } from 'types/entities/Company'
import {
    UiTable,
    UiTableContainer,
    UiTableHead,
    UiTableHeaderCell,
} from 'components/Ui/UiTable'
import CompanyActivityLogList from './CompanyActivityLogComponents/CompanyActivityLogList'
import { ActivityItem } from './CompanyActivityLogComponents/CompanyActivityLogItem'

interface CompanyActivityLogProps {
    company: Company
}

const CompanyActivityLog: React.FC<CompanyActivityLogProps> = ({ company }) => {
    const { t } = useTranslation()

    const activities = useMemo(() => {
        const list: ActivityItem[] = []

        // Add leads
        if (company.leads) {
            company.leads.forEach((l) => {
                list.push({
                    id: l.id,
                    type: 'lead',
                    date: l.created_at || '',
                    title: l.name || `${t('Lead')} #${l.id}`,
                    status: l.status || '',
                })
            })
        }

        // Add offers
        if (company.offers) {
            company.offers.forEach((o) => {
                list.push({
                    id: o.id,
                    type: 'offer',
                    date: o.created_at || '',
                    title: `${t('Offer')} #${o.id} (${o.guid?.slice(0, 8) || ''})`,
                    status: o.status || '',
                })
            })
        }

        // Add bookings (if available in future or from other source)
        // Currently company entity might not have bookings directly if not populated

        return list.sort(
            (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
        )
    }, [company, t])

    return (
        <UiTableContainer className="CompanyActivityLog">
            <UiTable>
                <UiTableHead>
                    <UiTableHeaderCell align="left">
                        {t('Date')}
                    </UiTableHeaderCell>
                    <UiTableHeaderCell align="left">
                        {t('Type')}
                    </UiTableHeaderCell>
                    <UiTableHeaderCell align="left">
                        {t('Activity')}
                    </UiTableHeaderCell>
                    <UiTableHeaderCell align="left">
                        {t('Status')}
                    </UiTableHeaderCell>
                </UiTableHead>
                <CompanyActivityLogList
                    activities={activities}
                    emptyLabel={t('No activities found')}
                />
            </UiTable>
        </UiTableContainer>
    )
}

export default CompanyActivityLog
